/**
 * Adapter pour imprimantes EPSON via Web USB
 * Compatible TM-T20, TM-M30, TM-T88, etc.
 *
 * Nécessite navigateur compatible Web USB (Chrome, Edge, Opera)
 */

import {
  PrinterAdapter,
  PrintJob,
  PrintLine,
  PrinterStatus,
  PrinterError,
} from './PrinterAdapter';
import { ESCPOSCommands } from './ESCPOSCommands';
import { logger } from '../../utils/logger';

export class EpsonUSBAdapter implements PrinterAdapter {
  readonly modelName = 'EPSON (USB)';
  readonly connectionType = 'USB' as const;

  private device: USBDevice | null = null;
  private endpoint: USBEndpoint | null = null;

  /**
   * Connecter à l'imprimante EPSON via Web USB
   */
  async connect(): Promise<void> {
    try {
      // Vérifier support Web USB
      if (!('usb' in navigator)) {
        throw new PrinterError(
          'Web USB non supporté par ce navigateur',
          'NOT_SUPPORTED'
        );
      }

      // Demander sélection de l'imprimante
      // Vendor ID EPSON: 0x04b8
      this.device = await navigator.usb.requestDevice({
        filters: [{ vendorId: 0x04b8 }], // EPSON
      });

      if (!this.device) {
        throw new PrinterError('Aucune imprimante sélectionnée', 'CONNECTION_ERROR');
      }

      // Ouvrir le device
      await this.device.open();

      // Sélectionner configuration (généralement 1)
      if (this.device.configuration === null) {
        await this.device.selectConfiguration(1);
      }

      // Claim l'interface (généralement 0)
      await this.device.claimInterface(0);

      // Trouver l'endpoint OUT
      const iface = this.device.configuration?.interfaces[0];
      const alternate = iface?.alternate;
      this.endpoint =
        alternate?.endpoints.find((ep) => ep.direction === 'out') ?? null;

      if (!this.endpoint) {
        throw new PrinterError(
          'Endpoint OUT non trouvé',
          'CONNECTION_ERROR'
        );
      }

      // Initialiser l'imprimante
      await this.sendRaw(ESCPOSCommands.init());

      logger.log('[EpsonUSBAdapter] Connecté avec succès');
    } catch (error) {
      if (error instanceof PrinterError) throw error;
      throw new PrinterError(
        'Erreur de connexion à l\'imprimante',
        'CONNECTION_ERROR',
        error as Error
      );
    }
  }

  /**
   * Déconnecter l'imprimante
   */
  async disconnect(): Promise<void> {
    if (this.device) {
      try {
        await this.device.releaseInterface(0);
        await this.device.close();
      } catch (error) {
        logger.warn('[EpsonUSBAdapter] Erreur déconnexion:', error);
      }
      this.device = null;
      this.endpoint = null;
    }
  }

  /**
   * Vérifier si connecté
   */
  isConnected(): boolean {
    return this.device !== null && this.endpoint !== null;
  }

  /**
   * Obtenir le statut de l'imprimante
   */
  async getStatus(): Promise<PrinterStatus> {
    if (!this.isConnected()) {
      return {
        connected: false,
        online: false,
        paperStatus: 'unknown',
      };
    }

    // TODO: Implémenter lecture statut réel via DLE EOT
    return {
      connected: true,
      online: true,
      paperStatus: 'ok',
    };
  }

  /**
   * Imprimer un ticket
   */
  async print(job: PrintJob): Promise<void> {
    if (!this.isConnected()) {
      throw new PrinterError('Imprimante non connectée', 'NOT_CONNECTED');
    }

    try {
      // Initialiser
      await this.sendRaw(ESCPOSCommands.init());

      // Traiter chaque ligne
      for (const line of job.lines) {
        await this.printLine(line);
      }

      // Avancer le papier et couper
      await this.sendRaw(ESCPOSCommands.lineFeed(4));
      await this.cut();

      logger.log('[EpsonUSBAdapter] Ticket imprimé avec succès');
    } catch (error) {
      throw new PrinterError(
        'Erreur lors de l\'impression',
        'PRINT_ERROR',
        error as Error
      );
    }
  }

  /**
   * Imprimer une ligne selon son type
   */
  private async printLine(line: PrintLine): Promise<void> {
    const commands: Uint8Array[] = [];

    // Type feed
    if (line.type === 'feed') {
      await this.sendRaw(ESCPOSCommands.lineFeed(line.feeds || 1));
      return;
    }

    // Alignement
    if (line.align) {
      commands.push(ESCPOSCommands.setAlign(line.align));
    }

    // Taille
    if (line.fontSize) {
      commands.push(ESCPOSCommands.setTextSize(line.fontSize));
    }

    // Gras
    if (line.bold) {
      commands.push(ESCPOSCommands.setBold(true));
    }

    // Soulignement
    if (line.underline) {
      commands.push(ESCPOSCommands.setUnderline(true));
    }

    // Contenu selon le type
    switch (line.type) {
      case 'text':
        if (line.content) {
          commands.push(ESCPOSCommands.text(line.content + '\n'));
        }
        break;

      case 'separator':
        commands.push(ESCPOSCommands.separator(line.content || '-', 42));
        break;

      case 'barcode':
        if (line.content) {
          commands.push(ESCPOSCommands.barcode(line.content));
        }
        break;

      case 'qrcode':
        if (line.content) {
          commands.push(ESCPOSCommands.qrcode(line.content));
        }
        break;
    }

    // Reset styles
    if (line.bold) {
      commands.push(ESCPOSCommands.setBold(false));
    }
    if (line.underline) {
      commands.push(ESCPOSCommands.setUnderline(false));
    }
    if (line.fontSize && line.fontSize !== 'normal') {
      commands.push(ESCPOSCommands.setTextSize('normal'));
    }
    if (line.align && line.align !== 'left') {
      commands.push(ESCPOSCommands.setAlign('left'));
    }

    // Envoyer toutes les commandes
    const combined = ESCPOSCommands.combine(...commands);
    await this.sendRaw(combined);
  }

  /**
   * Ouvrir le tiroir-caisse
   */
  async openCashDrawer(): Promise<void> {
    if (!this.isConnected()) {
      throw new PrinterError('Imprimante non connectée', 'NOT_CONNECTED');
    }

    await this.sendRaw(ESCPOSCommands.openCashDrawer());
    logger.log('[EpsonUSBAdapter] Tiroir-caisse ouvert');
  }

  /**
   * Couper le papier
   */
  async cut(): Promise<void> {
    if (!this.isConnected()) {
      throw new PrinterError('Imprimante non connectée', 'NOT_CONNECTED');
    }

    await this.sendRaw(ESCPOSCommands.cut());
  }

  /**
   * Envoyer des données brutes à l'imprimante
   */
  private async sendRaw(data: Uint8Array): Promise<void> {
    if (!this.device || !this.endpoint) {
      throw new PrinterError('Imprimante non connectée', 'NOT_CONNECTED');
    }

    try {
      // Convertir en ArrayBuffer pour compatibilité Web USB
      const buffer = data.buffer as ArrayBuffer;
      await this.device.transferOut(this.endpoint.endpointNumber, buffer);
    } catch (error) {
      throw new PrinterError(
        'Erreur d\'envoi de données à l\'imprimante',
        'PRINT_ERROR',
        error as Error
      );
    }
  }
}
